<template>
  <div
    v-if="prizes"
    class="prize-list"
  >
    <v-dialog
      v-model="prizesDialog"
      max-width="1000px"
      scrollable
      persistent
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="view-prizes-button"
          v-bind="attrs"
          v-on="on"
        >
          View prizes
        </v-btn>
      </template>
      <v-card class="prizes-dialog">
        <v-card-title>
          Prize List
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-form>
            <v-container
              v-if="prizes.length"
              class="prize-list-container"
              @drop="onDrop($event)"
              @dragenter.prevent
              @dragover.prevent
            >
              <v-row
                v-for="(prize, index) in prizes"
                :key="prize.id"
                class="prize-card row"
                :class="{ 'odd': index % 2 === 0 }"
                :draggable="editable"
                @dragstart="editable && onDragStart($event, prize)"
                @dragover="editable && onDragOver($event, index)"
              >
                <v-col
                  v-if="editable"
                  cols="1"
                >
                  <v-btn
                    id="`move-prize-up-${prize}`"
                    class="drag-item"
                    text
                    title="Move prize"
                  >
                    <span class="material-symbols-rounded">
                      menu
                    </span>
                  </v-btn>
                </v-col>
                <v-col
                  cols="11"
                >
                  <a
                    :href="prize.link"
                    target="_blank"
                    :title="prize.name"
                    class="prize-name"
                  >{{ prize.name }}</a>
                </v-col>
                <v-col
                  v-if="prize?.claimedBy?.name"
                  cols="11"
                >
                  <div
                    class="won-by-name"
                  >
                    {{ prize?.claimedBy?.name }}
                  </div>
                </v-col>
              </v-row>
            </v-container>
            <v-container
              v-else
            >
              <p>Stay tuned! Prizes coming soon!</p>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions
          class="bottom-section"
        >
          <v-btn
            color="primary"
            text
            class="cancel-button"
            @click="closeDialog()"
          >
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="editable && prizes.length > 0"
            color="primary"
            text
            class="update-prize-preferences-button"
            @click="editPrizeOrder()"
          >
            Update prize preferences
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="displayAreYouSure"
      persistent
      max-width="300"
    >
      <v-card>
        <v-card-title class="text-h5">
          Are you sure?
        </v-card-title>
        <v-card-text>
          You have unsaved changes to the Prize List.
          Are you sure you want to close Prizes?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="handleAreYouSure(true)"
          >
            Yes
          </v-btn>
          <v-btn
            text
            @click="handleAreYouSure(false)"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PrizesClient from '@/api/PrizesClient';
import { mapGetters } from 'vuex';

export default {
  name: 'PrizeList',
  props: {
    currentUser: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      prizes: [],
      prizesOrig: [],
      listenersAssigned: false,
      dragSourceIndex: null,
      dragTargetIndex: null,
      isChanged: false,
      displayAreYouSure: false,
      prizesDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      currentJwtToken: 'currentJwtToken',
      currentHackathonId: 'currentHackathonId',
    }),
    editable() {
      // in order to be editable, user must be on a team,
      // and event must be in either Hacking or Judging state
      return this.currentUser.team !== ''
        && (this.$store.state.eventState === 'Hacking'
        || this.$store.state.eventState === 'Judging');
    },
  },
  async mounted() {
    await this.getPrizes();
    this.prizesOrig = [...this.prizes];
  },
  updated() {
    if (!this.listenersAssigned) {
      const draggables = document.querySelectorAll('[draggable]');

      draggables.forEach((draggable) => {
        draggable.addEventListener('dragstart', () => {
          draggable.classList.add('dragging');
        });

        draggable.addEventListener('dragend', () => {
          draggable.classList.remove('dragging');
        });
      });

      document.addEventListener('dragleave', () => {
        this.killDragOver();
      });

      this.listenersAssigned = true;
    }
  },
  methods: {
    closeDialog() {
      if (this.isChanged) {
        this.displayAreYouSure = true;
      } else {
        this.prizesDialog = false;
        this.displayAreYouSure = false;
      }
    },
    handleAreYouSure(isClose) {
      if (isClose) {
        this.isChanged = false;
        this.prizesDialog = false;
        this.prizes = [...this.prizesOrig];
      }
      this.displayAreYouSure = false;
    },
    async getPrizes() {
      /* eslint-disable-next-line max-len */
      const prizesClient = new PrizesClient(this.currentJwtToken, this.currentHackathonId);
      try {
        const allPrizes = await prizesClient.getPrizes();
        this.prizes = allPrizes;
      } catch (err) {
        /* eslint-disable-next-line no-alert */
        alert('Error getting prizes. Please reach out to an event admin for assistance.');
      }
    },
    async editPrizeOrder() {
      const updatedPrizeList = this.prizes.map((p) => p.id);
      /* eslint-disable-next-line max-len */
      const prizesClient = new PrizesClient(this.currentJwtToken, this.currentHackathonId);
      try {
        await prizesClient.updatePrizeOrder(updatedPrizeList);
        this.isChanged = false;
        this.prizesOrig = [...this.prizes];
      } catch (err) {
        /* eslint-disable-next-line no-alert */
        alert('Error updating prize order. Please reach out to an event admin for assistance.');
      }
      this.prizesDialog = false;
    },
    // --- Drag and drop - Start
    onDragStart(event, prize) {
      const { dataTransfer } = event;
      dataTransfer.dropEffect = 'move';
      dataTransfer.effectAllowed = 'move';
      const index = this.prizes.findIndex((prizeParam) => prizeParam.id === prize.id);
      this.dragSourceIndex = index;
    },
    onDragOver(event, index) {
      if (this.dragSourceIndex !== index) {
        this.dragTargetIndex = index;
        this.killDragOver();
        document
          .querySelector(`.prize-list-container .row:nth-child(${index + 1})`)
          .classList.add('drag-over');
      } else if (this.dragSourceIndex === index) {
        this.killDragOver();
        this.dragTargetIndex = null;
      }
    },
    onDrop() {
      if (this.dragTargetIndex !== null) {
        // remove dragged item
        const [draggedPrize] = this.prizes.splice(this.dragSourceIndex, 1);
        // adjust target index
        if (this.dragTargetIndex > this.dragSourceTarget) {
          this.dragTargetIndex -= 1;
        }
        // insert dragged item
        this.prizes.splice(this.dragTargetIndex, 0, draggedPrize);
        this.isChanged = true;
      }

      this.dragTargetIndex = null;
      this.dragSourceIndex = null;
      this.killDragOver();
    },
    onDragLeave() {
      this.killDragOver();
    },
    killDragOver() {
      const oldOver = document.querySelector('.drag-over');
      if (oldOver) {
        oldOver.classList.remove('drag-over');
      }
    },
    // --- Drag and drop - End
  },
};
</script>

<style lang="scss" scoped>
.prize-list {
  position: relative;
  display: inline;
}

.view-prizes-button {
  background-color: #004e7d!important;
  color: #fff!important;

  ::v-deep .v-btn__content {
    text-transform: uppercase;
  }
}

.prize-list-container {
  margin-top: 20px;
}

.v-card__title {
  padding-left: .5em;
  padding-right: .5em;
}

.v-card__subtitle {
  padding-left: .75em;
  padding-right: .75em;
}

.v-btn {
  color: #004e7d;
  text-transform: none;
}

.row {
  background-color: #fafafa;
  min-height: 60px;
}

.row.odd {
  background-color: #f2f2f2;
}

.prize-name {
  position: relative;
  top: 8px;
}

.update-prize-preferences-button {
  text-transform: none;
  background-color: #004e7d !important;
  color: #fff !important;
}

.cancel-button {
  text-transform: none;
  color: #004e7d !important;
}

::v-deep a {
  color: #004e7d;
  font-weight: bolder;
  text-decoration: none;
}

::v-deep a:hover {
  text-decoration: underline;
}

.drag-item {
  cursor: move;
}
[draggable].dragging {
  background-color: rgb(133, 157, 186);
}
.drag-over.drag-over {
  background-color: #f3ac8b;
}
</style>
